import { Input } from "@headlessui/react";
import DateRangePicker, { DateRange } from "rsuite/DateRangePicker";
import "rsuite/DateRangePicker/styles/index.css";
import frFR from "rsuite/locales/fr_FR";
import "@/components/shared/daterange.style.css";
import { StyledSelectWoController } from "@/components/styled/StyledSelectWoController";
import { predefinedRanges } from "@/lib/predefinedRanges";
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Search, ListFilter } from "lucide-react";
import { useExpeditionFilters } from "../pendingExpeditions/useExpeditionFilters.reducer";
import { CountryCombobox } from "@/components/ui/customs/country-combobox";
import { useDebounce } from "@/hooks/useDebounce";
import { Button } from "@/components/ui/button";
import clsx from "clsx";
import { toEndOfDay, toStartOfDay } from "@/lib/utils";
import { usePackagingFilterOptions } from "@/hooks/useEnumOptions";

type FiltersProps = {
  filters: ReturnType<typeof useExpeditionFilters>["filters"];
  actions: ReturnType<typeof useExpeditionFilters>["actions"];
};

export const ClientExpeditionsFilters = ({ filters, actions }: FiltersProps) => {
  const { t } = useTranslation("boExpeditions");
  const packagingOptions = usePackagingFilterOptions();
  const [isVisible, setIsVisible] = useState(false);
  const [internalSearch, setInternalSearch] = useState(filters.search);
  const [internalTrackingCode, setInternalTrackingCode] = useState(filters.trackingCode);
  const debouncedInternalSearch = useDebounce(internalSearch, 300);
  const debouncedInternalTrackingCode = useDebounce(internalTrackingCode, 300);

  // Sync debounced internal search with actual filters
  useEffect(() => {
    if (debouncedInternalSearch !== filters.search) {
      actions.setSearch(debouncedInternalSearch);
    }
  }, [debouncedInternalSearch, filters.search, actions]);

  useEffect(() => {
    if (debouncedInternalTrackingCode !== filters.trackingCode) {
      actions.setTrackingCode(debouncedInternalTrackingCode);
    }
  }, [debouncedInternalTrackingCode, filters.trackingCode, actions]);

  // Sync internal search when filters.search changes externally (e.g., from URL)
  useEffect(() => {
    setInternalSearch(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setInternalTrackingCode(filters.trackingCode);
  }, [filters.trackingCode]);

  // Convert date range for the DateRangePicker
  const validatedAtRange: DateRange | null = useMemo(() => {
    if (filters.validatedAt && filters.validatedAt.length === 2) {
      return [new Date(filters.validatedAt[0]), new Date(filters.validatedAt[1])];
    }
    return null;
  }, [filters.validatedAt]);

  const pickupDateRange: DateRange | null = useMemo(() => {
    if (filters.pickupDate && filters.pickupDate.length === 2) {
      return [new Date(filters.pickupDate[0]), new Date(filters.pickupDate[1])];
    }
    return null;
  }, [filters.pickupDate]);

  const deliveryFromDateRange: DateRange | null = useMemo(() => {
    if (filters.deliveryFromDate && filters.deliveryFromDate.length === 2) {
      return [new Date(filters.deliveryFromDate[0]), new Date(filters.deliveryFromDate[1])];
    }
    return null;
  }, [filters.deliveryFromDate]);

  const finalizedAtRange: DateRange | null = useMemo(() => {
    if (filters.finalizedAt && filters.finalizedAt.length === 2) {
      return [new Date(filters.finalizedAt[0]), new Date(filters.finalizedAt[1])];
    }
    return null;
  }, [filters.finalizedAt]);

  const deliveryDateRange: DateRange | null = useMemo(() => {
    if (filters.deliveryDate && filters.deliveryDate.length === 2) {
      return [new Date(filters.deliveryDate[0]), new Date(filters.deliveryDate[1])];
    }
    return null;
  }, [filters.deliveryDate]);

  const handleValidatedAtRangeChange = (newRange: DateRange | null) => {
    if (newRange && newRange.length === 2) {
      const start = toStartOfDay(newRange[0]);
      const end = toEndOfDay(newRange[1]);
      actions.setValidatedAtRange([start, end]);
    } else {
      actions.setValidatedAtRange(null);
    }
  };

  const handlePickupDateRangeChange = (newRange: DateRange | null) => {
    if (newRange && newRange.length === 2) {
      const start = toStartOfDay(newRange[0]);
      const end = toEndOfDay(newRange[1]);
      actions.setPickupDateRange([start, end]);
    } else {
      actions.setPickupDateRange(null);
    }
  };

  const handleDeliveryFromDateRangeChange = (newRange: DateRange | null) => {
    if (newRange && newRange.length === 2) {
      const start = toStartOfDay(newRange[0]);
      const end = toEndOfDay(newRange[1]);
      actions.setDeliveryFromDateRange([start, end]);
    } else {
      actions.setDeliveryFromDateRange(null);
    }
  };

  const handleFinalizedAtRangeChange = (newRange: DateRange | null) => {
    if (newRange && newRange.length === 2) {
      const start = toStartOfDay(newRange[0]);
      const end = toEndOfDay(newRange[1]);
      actions.setFinalizedAtRange([start, end]);
    } else {
      actions.setFinalizedAtRange(null);
    }
  };

  const handleDeliveryDateRangeChange = (newRange: DateRange | null) => {
    if (newRange && newRange.length === 2) {
      const start = toStartOfDay(newRange[0]);
      const end = toEndOfDay(newRange[1]);
      actions.setDeliveryDateRange([start, end]);
    } else {
      actions.setDeliveryDateRange(null);
    }
  };

  return (
    <div className="flex w-full flex-col justify-between gap-2">
      <div className="flex w-full flex-row gap-2">
        <div className="flex flex-1 flex-row items-center gap-3 rounded-lg border border-solid border-gray-300 bg-white px-3 py-2">
          <Search />
          <Input
            value={internalSearch}
            onChange={(e) => setInternalSearch(e.target.value)}
            className="w-full text-sm font-medium focus:outline-hidden"
            placeholder={t("clientExpeditionsFilters.searchPlaceholder")}
          />
        </div>
        <Button
          variant="outline"
          className="h-full rounded-lg border border-gray-300 bg-white p-3"
          onClick={() => setIsVisible(!isVisible)}
        >
          <ListFilter />
        </Button>
      </div>
      <div
        className={clsx(
          "w-full gap-2",
          isVisible ? "flex flex-col lg:grid lg:grid-cols-4" : "hidden",
        )}
      >
        <CountryCombobox
          value={filters.country}
          onValueChange={(e) => actions.setCountry(e)}
          placeholder={t("clientExpeditionsFilters.countryPlaceholder")}
          clearable
        />
        <DateRangePicker
          placeholder={t("clientExpeditionsFilters.validatedAtPlaceholder")}
          ranges={predefinedRanges}
          showOneCalendar
          locale={frFR.Calendar}
          value={validatedAtRange}
          onChange={handleValidatedAtRangeChange}
          format="dd/MM/yyyy"
          appearance="subtle"
          editable={false}
          character={t("clientExpeditionsFilters.rangeSeparator")}
          className="flex h-full w-full min-w-[200px] flex-row items-center justify-center gap-3 rounded-lg border border-solid border-gray-300 bg-white text-gray-300"
          size="lg"
        />
        <DateRangePicker
          placeholder={t("clientExpeditionsFilters.pickupDatePlaceholder")}
          ranges={predefinedRanges}
          showOneCalendar
          locale={frFR.Calendar}
          value={pickupDateRange}
          onChange={handlePickupDateRangeChange}
          format="dd/MM/yyyy"
          appearance="subtle"
          editable={false}
          character={t("clientExpeditionsFilters.rangeSeparator")}
          className="flex h-full w-full min-w-[200px] flex-row items-center justify-center gap-3 rounded-lg border border-solid border-gray-300 bg-white text-gray-300"
          size="lg"
        />
        <DateRangePicker
          placeholder={t("clientExpeditionsFilters.deliveryFromDatePlaceholder")}
          ranges={predefinedRanges}
          showOneCalendar
          locale={frFR.Calendar}
          value={deliveryFromDateRange}
          onChange={handleDeliveryFromDateRangeChange}
          format="dd/MM/yyyy"
          appearance="subtle"
          editable={false}
          character={t("clientExpeditionsFilters.rangeSeparator")}
          className="flex h-full w-full min-w-[200px] flex-row items-center justify-center gap-3 rounded-lg border border-solid border-gray-300 bg-white text-gray-300"
          size="lg"
        />
        <DateRangePicker
          placeholder={t("clientExpeditionsFilters.finalizedAtPlaceholder")}
          ranges={predefinedRanges}
          showOneCalendar
          locale={frFR.Calendar}
          value={finalizedAtRange}
          onChange={handleFinalizedAtRangeChange}
          format="dd/MM/yyyy"
          appearance="subtle"
          editable={false}
          character={t("clientExpeditionsFilters.rangeSeparator")}
          className="flex h-full w-full min-w-[200px] flex-row items-center justify-center gap-3 rounded-lg border border-solid border-gray-300 bg-white text-gray-300"
          size="lg"
        />
        <DateRangePicker
          placeholder={t("clientExpeditionsFilters.deliveryDatePlaceholder")}
          ranges={predefinedRanges}
          showOneCalendar
          locale={frFR.Calendar}
          value={deliveryDateRange}
          onChange={handleDeliveryDateRangeChange}
          format="dd/MM/yyyy"
          appearance="subtle"
          editable={false}
          character={t("clientExpeditionsFilters.rangeSeparator")}
          className="flex h-full w-full min-w-[200px] flex-row items-center justify-center gap-3 rounded-lg border border-solid border-gray-300 bg-white text-gray-300"
          size="lg"
        />
        <StyledSelectWoController
          placeholder={t("clientExpeditionsFilters.packagingPlaceholder")}
          isClearable={true}
          options={packagingOptions}
          onChange={(e) => actions.setPackagingType(e?.value || null)}
          className=""
          isSearchable={false}
          value={filters.packagingType ?? undefined}
        />
      </div>
    </div>
  );
};
